import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiClient } from '@/lib/apiClient'

import type {
  PublicContentBlock,
  PublicEvent,
  PublicFaq,
  PublicSponsor,
  PublicTeamMember,
} from './types'

/**
 * Accès à la surface publique.
 *
 * `Accept-Language` porte la langue choisie : c'est le serveur qui résout les
 * traductions, le front ne fait que transmettre la préférence.
 */

function useLocaleHeader(): { locale: string } {
  const { i18n } = useTranslation()
  return { locale: i18n.language }
}

export function usePublicEvent(slug?: string) {
  const { locale } = useLocaleHeader()
  const path = slug ? `/public/events/${slug}` : '/public/events/current'

  return useQuery({
    queryKey: ['public-event', slug ?? 'current', locale],
    queryFn: () => apiClient.get<PublicEvent>(path, { locale }),
    retry: 1,
  })
}

export function useEventContent(slug: string | undefined) {
  const { locale } = useLocaleHeader()

  return useQuery({
    queryKey: ['public-content', slug, locale],
    queryFn: () => apiClient.get<PublicContentBlock[]>(`/public/events/${slug}/content`, { locale }),
    enabled: Boolean(slug),
  })
}

/**
 * Les questions fréquentes. L'écran « Contenus » les écrit ; sans ce
 * crochet, aucune page du site ne les lisait — on remplissait une FAQ que
 * personne ne pouvait voir.
 */
export function useEventFaqs(slug: string | undefined) {
  const { locale } = useLocaleHeader()

  return useQuery({
    queryKey: ['public-faqs', slug, locale],
    queryFn: () => apiClient.get<PublicFaq[]>(`/public/events/${slug}/faqs`, { locale }),
    enabled: Boolean(slug),
  })
}

export function useEventSponsors(slug: string | undefined) {
  const { locale } = useLocaleHeader()

  return useQuery({
    queryKey: ['public-sponsors', slug, locale],
    queryFn: () => apiClient.get<PublicSponsor[]>(`/public/events/${slug}/sponsors`, { locale }),
    enabled: Boolean(slug),
  })
}

/**
 * L'équipe LeYAMO, telle qu'elle se présente au public.
 *
 * Rien à voir avec les comptes de l'équipe : ici on ne sert qu'un nom, un rôle
 * et une photo. Beaucoup de gens de l'équipe n'ont aucun compte, et plusieurs
 * comptes n'ont rien à faire sur une page publique.
 */
export function useEventTeam(slug: string | undefined) {
  const { locale } = useLocaleHeader()

  return useQuery({
    queryKey: ['public-team', slug, locale],
    queryFn: () => apiClient.get<PublicTeamMember[]>(`/public/events/${slug}/team`, { locale }),
    enabled: Boolean(slug),
  })
}

/** Ce que dit la bulle de l'accueil — deux nombres, rien de personnel. */
export interface LiveAudience {
  /** Faux : l'équipe a masqué la bulle (réglage `site.visitor_bubble`). */
  enabled: boolean
  /** Visiteurs depuis le début, comme l'écran Audience ; absent sous le seuil. */
  total_visitors: number | null
  /** Visiteurs actifs dans la fenêtre ; absent si le serveur ne peut pas le savoir. */
  online_now: number | null
  window_seconds: number
}

/**
 * Relu toutes les 25 s, et seulement tant que l'onglet est visible :
 * React Query suspend l'intervalle d'un onglet caché, et relit au retour.
 * Une erreur n'est pas retentée — la bulle disparaît, la page reste.
 */
export function useLiveAudience(slug: string | undefined) {
  return useQuery({
    queryKey: ['public-audience-live', slug],
    queryFn: () => apiClient.get<LiveAudience>(`/public/events/${slug}/audience/live`),
    enabled: Boolean(slug),
    refetchInterval: 25_000,
    refetchIntervalInBackground: false,
    staleTime: 20_000,
    retry: false,
  })
}
